import React, { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import clsx from 'clsx';

import {
    Avatar,
    Button,
    Glyphicon,
    StarRating,
    Text,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { sendTabEvent } from 'reaxl-analytics-handlers';

import vdpRatingReviewDuck from '@/ducks/vdp/vdpRatingReviewDuck';

import { RatingBars } from '@/components/RatingsReviews';

function ConsumerReview({
    consumerReview,
    isModal = false,
}) {
    const { consumerReviews } = useSelector(vdpRatingReviewDuck.selectors.getDuckState) || [];
    consumerReview = consumerReview ?? consumerReviews[0];
    const dispatch = useDispatch();

    const {
        anonymous,
        nicknameDisplay = '',
        submissionDate = '',
        year = '',
        make = '',
        model = '',
        ratingOverall,
        title = '',
        pros = '',
        cons = '',
        reviewText = '',
        areProsOrConsAvailable,
        recommend,
        ratings,
    } = consumerReview;

    const handleViewClick = useCallback((event) => {
        sendClick(sendTabEvent, event, {
            componentType: 'text-link',
            label: 'view-all-consumer-reviews',
        });
    }, []);

    const toggleShowModal = (event) => {
        dispatch(vdpRatingReviewDuck.creators.setShowReviewsModal(true));
        handleViewClick(event);
    };

    const renderProsAndCons = (
        <>
            {pros && (
                <div>
                    <Text
                        color="success"
                        weight="bold"
                        componentClass="div"
                    >
                        Pros
                    </Text>
                    {pros}
                </div>
            )}
            {cons && (
                <div>
                    <Text
                        color="danger"
                        weight="bold"
                        componentClass="div"
                    >
                        Cons
                    </Text>
                    {cons}
                </div>
            )}
        </>
    );

    const renderReviewText = (
        <div>
            {(!isModal && reviewText.length > 300) ? reviewText.slice(0, 299).concat('...') : reviewText}
        </div>
    );

    const renderReview = (areProsOrConsAvailable ? renderProsAndCons : (reviewText && renderReviewText));

    return (
        <div
            data-cmp="consumerReview"
            className={clsx('display-flex gap-4 flex-column', {
                'border-gray-light border-top-0 border-horizontal-0 margin-bottom-5 padding-bottom-4': isModal,
                'margin-top-5 margin-bottom-2': !isModal,
            })}
        >
            {/* User information */}
            <div className="display-flex align-items-center gap-4">
                {anonymous ? (
                    <span
                        style={{ fontSize: '40px', color: '#D3D3D3' }}
                        className="glyphicon glyphicon-user-circle-solid"
                    />
                ) : <Avatar text={nicknameDisplay} />}
                <div>
                    <Text
                        componentClass="div"
                        weight="bold"
                        className="text-capitalize"
                    >
                        {nicknameDisplay}
                    </Text>
                    <Text
                        size={200}
                        componentClass="div"
                        color="text-subdued-lighter"
                    >
                        {`${submissionDate.split(' ')[0]}`}
                        <span className="margin-horizontal-2">|</span>
                        {decodeURIComponent(`${year} ${make} ${model}`)}
                    </Text>
                </div>
            </div>

            {/* Star rating */}
            <div>
                <StarRating
                    value={ratingOverall}
                    color="#e8bb38"
                    className="text-size-500"
                />
                <Text
                    componentClass="div"
                    weight="bold"
                    className="text-capitalize"
                >
                    {title}
                </Text>
            </div>

            {isModal
                /* Display both Pros Cons and Content */
                ? (
                    <>
                        {areProsOrConsAvailable && renderProsAndCons}
                        {reviewText && renderReviewText}
                    </>
                )
                : renderReview}

            {/* Progress bar */}
            {isModal && (
                <RatingBars
                    ratings={ratings}
                    layout="horizontal"
                />
            )}

            {/* Recommends vehicle */}
            {recommend && (
                <div className="display-inline-flex">
                    <Glyphicon
                        glyph="success"
                        className="margin-right-2 text-success text-size-500"
                    />
                    <Text>Recommends this vehicle</Text>
                </div>
            )}

            {/* View all reviews modal button */}
            {!isModal && (
                <Button
                    bsStyle="text"
                    onClick={toggleShowModal}
                >
                    View all consumer reviews
                </Button>
            )}
        </div>
    );
}

export default React.memo(ConsumerReview);
